/**
 * CustomTextSearch - Single text field search filter (Default Target Margin style).
 * Pop-over: CLEAR ALL | text input | CANCEL, APPLY. Server-side filter on Apply.
 */
import React, { useState } from 'react';
import { TextField, ClickAwayListener, InputAdornment, Popper, Grid, Button, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { DisableAppScroll, EnableAppScroll } from 'app/layout/Layout';

const useStyles = makeStyles()(() => ({
  textField: { fontSize: 12, '& input::placeholder': { fontSize: 12 } },
  headerContainer: { padding: '12px 20px 6px' },
  popper: { marginTop: 4, zIndex: 5000 },
  paperDropdown: { boxShadow: '0px 2px 8px #0000004D' },
  button: { fontSize: '12px', fontWeight: 'bold', textTransform: 'none', '&:hover, &:focus': { background: 'none' }, opacity: 1 },
  actionContainer: { backgroundColor: '#ffffff', borderTop: '1px solid #cccccc', padding: '0px 8px', borderBottomLeftRadius: 4, borderBottomRightRadius: 4 },
  boxContainer: { backgroundColor: '#ffffff', padding: '12px 20px', minHeight: 56 },
  inputField: { width: '100%', minWidth: 260, '& .MuiInputBase-input': { fontSize: 12 } },
  inActive: { color: '#888888' },
  clearAllButton: { padding: 0 },
  actionButton: { padding: 12 },
}));

export const CustomTextSearch = (props: {
  value: string | null;
  onChange: (v: string | null) => void;
  apply: (v: string | null) => void;
  placeholder?: string;
  filterName?: string;
  applyOnClickAway?: boolean;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localValue, setLocalValue] = useState(props.value?.trim() ?? '');
  const open = Boolean(anchorEl);

  const calcWidth = () => {
    const m = (s: string) => (s?.length || 0) * 7.5;
    const base = props.placeholder || props.filterName || '';
    const text = props.value ? `(1) ${base}` : base;
    return Math.max(140, Math.min(340, Math.ceil(m(text) + 72)));
  };

  const clearAll = () => { setLocalValue(''); props.onChange(null); };
  const close = () => { setAnchorEl(null); EnableAppScroll(); };
  const cancel = () => { setLocalValue(props.value?.trim() ?? ''); close(); };
  const onApply = () => {
    const v = localValue.trim() || null;
    props.onChange(v);
    props.apply(v);
    close();
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!open) { setAnchorEl(e.currentTarget); setLocalValue(props.value?.trim() ?? ''); DisableAppScroll(); }
    else cancel();
  };

  const onClickAway = (e: MouseEvent | TouchEvent) => {
    if ((e.target as HTMLElement)?.type !== 'submit') {
      if (props.applyOnClickAway) { const v = localValue.trim() || null; if (String(props.value ?? '') !== String(v ?? '')) { props.onChange(v); props.apply(v); } close(); }
      else cancel();
    }
  };

  return (
    <div>
      <TextField
        value={props.value ? `(1) ${props.filterName || props.placeholder || ''}` : ''}
        placeholder={props.placeholder || ''}
        variant="outlined"
        size="small"
        className={classes.textField}
        inputProps={{ 'aria-label': 'text-search-field', readOnly: true }}
        onClick={handleOpen}
        style={{ width: calcWidth() }}
        InputProps={{
          endAdornment: <InputAdornment position="end"><KeyboardArrowDownIcon style={{ fontSize: 20, color: '#c1c1c1' }} /></InputAdornment>,
          style: { color: props.value ? '#555555' : undefined, borderRadius: 4, minHeight: '36.7px', fontSize: 12, fontWeight: 400 },
        }}
      />
      <Popper open={open} className={classes.popper} anchorEl={anchorEl} placement="bottom-start">
        <ClickAwayListener onClickAway={onClickAway}>
          <Paper className={classes.paperDropdown}>
            <Grid container justifyContent="flex-end" alignItems="center" className={classes.headerContainer}>
              <Button aria-label="clear-all" variant="text" color="primary" disableRipple disabled={!props.value && !localValue.trim()} onClick={clearAll} className={`${classes.button} ${classes.clearAllButton}`}>
                {t('search.filter.clearAll')}
              </Button>
            </Grid>
            <div className={classes.boxContainer}>
              <TextField className={classes.inputField} variant="outlined" size="small" placeholder={props.placeholder || ''} value={localValue} onChange={(e) => setLocalValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onApply()} autoFocus inputProps={{ 'aria-label': 'task-name-input' }} />
            </div>
            <div className={classes.actionContainer}>
              <Grid container justifyContent="flex-end" alignItems="center">
                <Button aria-label="cancel" variant="text" disableRipple onClick={cancel} className={`${classes.button} ${classes.actionButton} ${classes.inActive}`}>{t('search.filter.cancel')}</Button>
                <Button aria-label="apply" variant="text" color="primary" disableRipple onClick={onApply} className={`${classes.button} ${classes.actionButton}`}>{t('search.filter.apply')}</Button>
              </Grid>
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};
